/**
 * DashboardSettingsModal - Modal to configure meeting dashboard quick links
 * Allows adding, editing, reordering, and deleting dashboard items
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, FolderOpen, Link2, StickyNote, Plus, Trash2, 
  GripVertical, Loader2, X, Save
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Item type options
const ITEM_TYPES = [
  { value: 'note', label: 'Note', icon: StickyNote, description: 'Add a quick note or reminder' },
  { value: 'link', label: 'Link', icon: Link2, description: 'Link to a page or external URL' },
  { value: 'document', label: 'Document', icon: FileText, description: 'Link to a board document' },
  { value: 'divider', label: 'Divider', icon: FolderOpen, description: 'Section divider with label' },
];

// Icon options
const ICON_OPTIONS = [
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'FolderOpen', label: 'Folder', Icon: FolderOpen },
  { value: 'Link2', label: 'Link', Icon: Link2 },
  { value: 'StickyNote', label: 'Note', Icon: StickyNote },
];

// Item Editor Component
const ItemEditor = ({ item, onSave, onCancel, documents = [] }) => {
  const [formData, setFormData] = useState({
    type: item?.type || 'link',
    title: item?.title || '',
    content: item?.content || '',
    url: item?.url || '',
    document_id: item?.document_id || '',
    icon: item?.icon || 'FileText',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (formData.type === 'link' && !formData.url.trim()) {
      toast.error('URL is required for link items');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...formData,
        id: item?.id,
      });
    } finally {
      setSaving(false);
    }
  };

  const selectedType = ITEM_TYPES.find(t => t.value === formData.type);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
        <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ITEM_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  <type.icon className="h-4 w-4" />
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedType && (
          <p className="mt-1 text-xs text-gray-500">{selectedType.description}</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Enter title..."
          required
        />
      </div>

      {/* Icon Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
        <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ICON_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                <div className="flex items-center gap-2">
                  <opt.Icon className="h-4 w-4" />
                  <span>{opt.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Type-specific fields */}
      {formData.type === 'note' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
          <Textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="Enter note content..."
            rows={4}
          />
        </div>
      )}

      {formData.type === 'link' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
          <Input
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="/governance/documents or https://..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Use relative paths (e.g., /governance) for internal pages, or full URLs for external links
          </p>
        </div>
      )}

      {formData.type === 'document' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Document</label>
          <Select 
            value={formData.document_id} 
            onValueChange={(v) => setFormData({ ...formData, document_id: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a document..." />
            </SelectTrigger>
            <SelectContent>
              {documents.length === 0 ? (
                <div className="px-2 py-4 text-sm text-gray-500 text-center">
                  No documents available
                </div>
              ) : (
                documents.map(doc => (
                  <SelectItem key={doc.id} value={doc.id}>
                    {doc.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {item?.id ? 'Update' : 'Add'} Item
        </Button>
      </div>
    </form>
  );
};

// Item Row in List
const ItemRow = ({ item, onEdit, onDelete, onDragStart, onDragOver, onDrop }) => {
  const IconComponent = ICON_OPTIONS.find(i => i.value === item.icon)?.Icon || FileText;

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, item.id)}
      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg group hover:bg-gray-100 cursor-move"
    >
      <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
      <IconComponent className="h-4 w-4 text-purple-600" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
        <p className="text-xs text-gray-500 capitalize">{item.type}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(item)}>
          <FileText className="h-3.5 w-3.5" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-7 w-7 p-0 text-red-500 hover:text-red-600" 
          onClick={() => onDelete(item.id)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

// Main Modal Component
const DashboardSettingsModal = ({ 
  open, 
  onClose, 
  editItem = null,
  onSettingsChanged,
  settingsEndpointBase = '/board/dashboard-settings',
  documentsEndpoint = '/board/documents'
}) => {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'edit'
  const [currentItem, setCurrentItem] = useState(null);
  const [draggedId, setDraggedId] = useState(null);

  // Fetch settings and documents
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, docsRes] = await Promise.all([
        api.get(`${settingsEndpointBase}/default`),
        api.get(documentsEndpoint).catch(() => ({ data: { documents: [] } })),
      ]);
      
      setSettings(settingsRes.data);
      setItems(settingsRes.data.items || []);
      setDocuments(docsRes.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load dashboard settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
      // If editItem is provided, go directly to edit view
      if (editItem) {
        setCurrentItem(editItem);
        setView('edit');
      } else {
        setView('list');
        setCurrentItem(null);
      }
    }
  }, [open, fetchData, editItem]);

  // Add or update item
  const handleSaveItem = async (itemData) => {
    if (!settings?.id) return;

    try {
      if (itemData.id) {
        // Update existing item
        await api.put(`${settingsEndpointBase}/${settings.id}/items/${itemData.id}`, itemData);
        setItems(prev => prev.map(i => i.id === itemData.id ? { ...i, ...itemData } : i));
        toast.success('Item updated');
      } else {
        // Add new item
        const response = await api.post(`${settingsEndpointBase}/${settings.id}/items`, {
          ...itemData,
          order: items.length,
        });
        setItems(prev => [...prev, response.data.item]);
        toast.success('Item added');
      }
      
      setView('list');
      setCurrentItem(null);
      if (onSettingsChanged) onSettingsChanged();
    } catch (err) {
      toast.error('Failed to save item');
    }
  };

  // Delete item
  const handleDeleteItem = async (itemId) => {
    if (!settings?.id) return;
    
    if (!window.confirm('Delete this item?')) return;

    try {
      await api.delete(`${settingsEndpointBase}/${settings.id}/items/${itemId}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success('Item deleted');
      if (onSettingsChanged) onSettingsChanged();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  // Drag and drop reordering
  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId || !settings?.id) return;

    const draggedIndex = items.findIndex(i => i.id === draggedId);
    const targetIndex = items.findIndex(i => i.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder items
    const newItems = [...items];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    // Update order property
    const reorderedItems = newItems.map((item, index) => ({ ...item, order: index }));
    setItems(reorderedItems);
    setDraggedId(null);

    // Save to server
    try {
      await api.put(`${settingsEndpointBase}/${settings.id}/items/reorder`, {
        item_ids: reorderedItems.map(i => i.id),
      });
      if (onSettingsChanged) onSettingsChanged();
    } catch (err) {
      console.error('Failed to reorder items:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {view === 'edit' ? (currentItem?.id ? 'Edit Item' : 'Add Item') : 'Configure Quick Links'}
          </DialogTitle>
          <DialogDescription>
            {view === 'edit' 
              ? 'Configure the quick link item' 
              : 'Add, edit, and reorder your meeting dashboard quick links'
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : view === 'list' ? (
          <div className="flex-1 overflow-y-auto">
            {/* Items List */}
            {items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Link2 className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No quick links yet</p>
                <p className="text-xs text-gray-400 mt-1">Add links to quickly access documents and pages</p>
              </div>
            ) : (
              <div className="space-y-2 py-2">
                {items
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((item) => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      onEdit={(item) => {
                        setCurrentItem(item);
                        setView('edit');
                      }}
                      onDelete={handleDeleteItem}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    />
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-2">
            <ItemEditor
              item={currentItem}
              documents={documents}
              onSave={handleSaveItem}
              onCancel={() => {
                setView('list');
                setCurrentItem(null);
              }}
            />
          </div>
        )}

        {view === 'list' && (
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={() => {
              setCurrentItem(null);
              setView('edit');
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DashboardSettingsModal;
